import '../../AssistantSettingsTheme.css';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Radio, RadioGroup, Select, Switch, TextField, Tooltip, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import React from 'react';

import useChannelForm from '../hooks/useChannelForm';
import TextFieldWithModal from '../TextFieldWithModal';
import InstructionHistoryButton from '../../InstructionHistoryButton';
import useIsMobile from '../../../useIsMobile';

import LlmConfigSettings from './LlmConfigSettings';
import VoiceConfigSettings from './VoiceConfigSettings';

const PhoneChannelSettings =
    ({initialSettings, agentGreeting = '', agentId = null, channelType = 'VAPI', onSave, saving, error, success}) => {
      const {values, handleChange, hasChanges, getValuesForSave} =
          useChannelForm(initialSettings);
      const {enqueueSnackbar} = useSnackbar();
      const isMobile = useIsMobile();

      const handleSaveClick = () => {
        const payload =
            getValuesForSave({pruneUnchangedPaths: ['voice_config']});
        onSave('VAPI', payload);
      };

      const handleCopyToClipboard = () => {
        const phoneNumber = initialSettings?.phone_number;
        if (phoneNumber) {
          navigator.clipboard.writeText(phoneNumber);
          enqueueSnackbar(
              'Phone number copied to clipboard', {variant: 'success'});
        }
      };
      const channelGreeting = values.greeting ?? '';

      const handleAddWebhook = () => {
        const newWebhooks = [
          ...(values.event_webhooks || []),
          {event: 'end-of-call-report', url: '', enabled: true}
        ];
        handleChange('event_webhooks', newWebhooks);
      };

      const handleUpdateWebhook = (index, field, value) => {
        const newWebhooks = [...(values.event_webhooks || [])];
        newWebhooks[index] = {...newWebhooks[index], [field]: value};
        handleChange('event_webhooks', newWebhooks);
      };

      const handleRemoveWebhook = (index) => {
        const newWebhooks =
            (values.event_webhooks || []).filter((_, i) => i !== index);
        handleChange('event_webhooks', newWebhooks);
      };

      return (
        <Box className='assistantSettingsRoot' sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
          {error && <Alert severity='error'>{error}</Alert>}
          {success && <Alert severity='success'>{success}</Alert>}

          {/* --- General Settings --- */}

          {/* Updated Phone Number Display */}
          <TextField
            label='Phone Number'
            value={initialSettings?.phone_number || 'Not available'}
            fullWidth
            variant='outlined'
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position='end'>
                  <Tooltip title='Copy to clipboard'>
                    <IconButton onClick={handleCopyToClipboard} edge='end' className='assistantIconButtonNeutral'>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <TextFieldWithModal
            label='Initial Greeting'
            value={channelGreeting}
            onChange={(e) => handleChange('greeting', e.target.value)}
            rows={2}
          />

          {/* --- Instructions Field --- */}
          <TextFieldWithModal
            label='Instructions'
            value={values.instructions || ''}
            onChange={(e) => handleChange('instructions', e.target.value)}
            rows={10}
            headerAction={
              agentId ? (
                <InstructionHistoryButton
                  agentId={agentId}
                  channelType={channelType}
                  onRestore={(value) => handleChange('instructions', value)}
                />
              ) : null
            }
          />

          {/* --- Expandable Advanced Settings --- */}
          <Accordion sx={{mt: 1}}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='advanced-settings-content'
              id='advanced-settings-header'
            >
              <Typography>Advanced Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                <VoiceConfigSettings values={values} onChange={handleChange} />
                <LlmConfigSettings values={values} onChange={handleChange} channel="vapi" />

                {/* --- Notifications & Webhooks --- */}
                <Box className='assistantSettingsPanel' sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                  <Typography variant='subtitle1'>Notifications & Webhooks</Typography>

                  <FormControl component='fieldset'>
                    <Typography variant='caption' color='text.secondary' sx={{mb: 0.5}}>
                      Notification Mode
                    </Typography>
                    <RadioGroup
                      row={!isMobile}
                      value={values.notification_mode || 'general'}
                      onChange={(e) => handleChange('notification_mode', e.target.value)}
                    >
                      <FormControlLabel value='general' control={<Radio />} label={isMobile ? 'Email & SMS' : 'General (Email & SMS)'} />
                      <FormControlLabel value='advanced' control={<Radio />} label={isMobile ? 'Event Webhooks' : 'Advanced (Event Webhooks)'} />
                    </RadioGroup>
                  </FormControl>

                  {values.notification_mode === 'advanced' && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                      {!isMobile && (
                        <Typography variant='body2' color='text.secondary'>
                          When enabled, events matching these rules will be sent to the specified webhook instead of triggering general email/SMS notifications.
                        </Typography>
                      )}

                      {(values.event_webhooks || []).map((webhook, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isMobile ? 1.5 : 2,
                            alignItems: isMobile ? 'stretch' : 'flex-start',
                            p: isMobile ? 1.5 : 2,
                            border: '1px solid',
                            borderColor: '#d9d9d9',
                            borderRadius: 3,
                          }}
                        >
                          <FormControl size='small' sx={{minWidth: isMobile ? 0 : 200}} fullWidth={isMobile}>
                            <InputLabel>Event Type</InputLabel>
                            <Select
                              value={webhook.event}
                              label='Event Type'
                              onChange={(e) => handleUpdateWebhook(index, 'event', e.target.value)}
                            >
                              <MenuItem value='end-of-call-report'>End of Call Report</MenuItem>
                              <MenuItem value='status-update'>Status Update</MenuItem>
                            </Select>
                          </FormControl>

                          <TextField
                            size='small'
                            label='Webhook URL'
                            value={webhook.url}
                            onChange={(e) => handleUpdateWebhook(index, 'url', e.target.value)}
                            fullWidth
                            placeholder='https://api.agentops.ai/v1/...'
                          />

                          {/* Mobile keeps the toggle and delete on one line under the fields */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: isMobile ? 'space-between' : 'flex-start',
                              gap: 1,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={webhook.enabled}
                                  onChange={(e) => handleUpdateWebhook(index, 'enabled', e.target.checked)}
                                  size='small'
                                />
                              }
                              label='Enabled'
                              labelPlacement={isMobile ? 'end' : 'top'}
                              sx={{m: 0}}
                            />

                            <Tooltip title='Remove webhook rule'>
                              <IconButton
                                onClick={() => handleRemoveWebhook(index)}
                                className='assistantIconButtonDanger'
                                sx={{mt: isMobile ? 0 : 1}}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}

                      <Button
                        startIcon={<AddRoundedIcon />}
                        onClick={handleAddWebhook}
                        variant='outlined'
                        size='small'
                        className='assistantOutlineButton'
                        sx={{alignSelf: 'flex-start'}}
                      >
                        Add Webhook Rule
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Button
            variant='contained'
            color='primary'
            onClick={handleSaveClick}
            disabled={!hasChanges || saving || success}
            className='assistantPrimaryButton assistantButtonAlignEnd'
            sx={{mt: 2}}
          >
            {saving ? 'Saving...' : isMobile ? 'Save' : 'Save Phone Channel Settings'}
          </Button>
        </Box>
      );
    };

export default PhoneChannelSettings;
